import React from 'react';
import { Box, Container, Heading, Text, VStack, SimpleGrid, Badge } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaQuoteLeft } from 'react-icons/fa';

const MotionBox = motion(Box);

const AIMasteryTestimonials = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="brand.50">
      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Title */}
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              fontWeight="900"
              color="brand.800"
            >
              {data.title}
            </Heading>
            {data.subtitle && (
              <Text fontSize="lg" color="brand.600" maxW="3xl">
                {data.subtitle}
              </Text>
            )}
          </VStack>

          {/* Testimonials Grid */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
            {data.stories?.map((story, index) => (
              <MotionBox
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box
                  p={8}
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                  position="relative"
                  h="full"
                  _hover={{
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(128,90,213,0.2)',
                  }}
                  transition="all 0.3s"
                >
                  <VStack spacing={4} align="flex-start">
                    {/* Quote Icon */}
                    <Box p={2} bg="purple.50" borderRadius="lg">
                      <FaQuoteLeft size={24} color="#805ad5" />
                    </Box>

                    {/* Quote */}
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="brand.700"
                      fontStyle="italic"
                    >
                      "{story.quote}"
                    </Text>

                    {/* Author */}
                    <Text
                      fontSize="sm"
                      color="brand.500"
                      fontWeight="600"
                    >
                      — {story.author}
                    </Text>

                    {/* New: Outcome & Metric */}
                    {(story.outcome || story.metric) && (
                      <VStack spacing={2} align="stretch" pt={2} w="full" borderTop="1px solid" borderColor="gray.200">
                        {story.outcome && (
                          <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                            {story.outcome}
                          </Badge>
                        )}
                        {story.metric && (
                          <Text fontSize="sm" fontWeight="700" color="accent.green">
                            {story.metric}
                          </Text>
                        )}
                      </VStack>
                    )}

                    {/* Visual Placeholder */}
                    {story.visualPlaceholder && (
                      <Box
                        p={3}
                        bg="gray.100"
                        borderRadius="md"
                        borderStyle="dashed"
                        borderWidth="1px"
                        borderColor="gray.300"
                        w="full"
                        textAlign="center"
                      >
                        <Text color="gray.600" fontSize="xs" fontStyle="italic">
                          {story.visualPlaceholder}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>

          {/* Aggregate Metrics */}
          {data.aggregateMetrics && (
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              w="full"
            >
              <Box p={8} bgGradient="linear(to-r, purple.600, purple.800)" borderRadius="2xl">
                <VStack spacing={6}>
                  <Heading size="lg" color="white" textAlign="center">
                    {data.aggregateMetrics.title}
                  </Heading>
                  
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} w="full">
                    {data.aggregateMetrics.stats?.map((stat, index) => (
                      <VStack key={index} spacing={2} textAlign="center">
                        <Text fontSize="4xl" fontWeight="900" color="accent.gold">
                          {stat.metric}
                        </Text>
                        <Text fontSize="sm" color="white" opacity={0.9}>
                          {stat.description}
                        </Text>
                      </VStack>
                    ))}
                  </SimpleGrid>
                </VStack>

                {data.aggregateMetrics.visualPlaceholder && (
                  <Box
                    mt={4}
                    p={3}
                    bg="whiteAlpha.200"
                    borderRadius="md"
                    textAlign="center"
                  >
                    <Text color="whiteAlpha.800" fontSize="xs" fontStyle="italic">
                      {data.aggregateMetrics.visualPlaceholder}
                    </Text>
                  </Box>
                )}
              </Box>
            </MotionBox>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AIMasteryTestimonials;
